import { getFirmwareApiGateway } from 'common';
import { RefreshCw, Power } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Text, YStack, XStack, Circle } from 'tamagui';
import { tokens } from 'tamagui-config';

import { StyledCard } from './styles';
import { HttpClient } from '../../../utils/httpClient';

export const DevOptions: React.FC = () => {
  const { t } = useTranslation();
  const [restarting, setRestarting] = useState(false);
  const [shuttingDown, setShuttingDown] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showPinEntry, setShowPinEntry] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinEntryMode, setPinEntryMode] = useState<'restart' | 'shutdown'>('restart');
  
  const PIN_LENGTH = 4;

  // Handle restart button click - show PIN keypad
  const handleRestartClick = () => {
    setShowPinEntry(true);
    setPinEntryMode('restart');
    setPin('');
    setPinError(null);
  };
  
  // Handle shutdown button click - show PIN keypad
  const handleShutdownClick = () => {
    setShowPinEntry(true);
    setPinEntryMode('shutdown');
    setPin('');
    setPinError(null);
  };

  // Handle PIN entry
  const handleNumberClick = (number: string) => {
    if (pin.length < PIN_LENGTH) {
      const newPin = pin + number;
      setPin(newPin);
      
      // If PIN is complete, validate and perform the action
      if (newPin.length === PIN_LENGTH) {
        setTimeout(() => {
          if (pinEntryMode === 'restart') {
            handleRestartWithPin(newPin);
          } else if (pinEntryMode === 'shutdown') {
            handleShutdownWithPin(newPin);
          }
        }, 500);
      }
    }
  };

  // Handle device restart with entered PIN
  const handleRestartWithPin = async (enteredPin: string) => {
    if (restarting) return;

    try {
      setRestarting(true);
      
      // Use the entered PIN as the unlock code
      const httpClient = new HttpClient(getFirmwareApiGateway());
      await httpClient.post('/device/restart', { 
        unlock_code: enteredPin,
      });
      
      // Close PIN dialog and show success message
      setShowPinEntry(false);
      setMessage(t('settings.developer.restartInitiated'));
      
      // We might lose connection to the device after restart
      setTimeout(() => {
        setMessage(t('settings.developer.waitingForDevice'));
      }, 3000);
      
    } catch (error) {
      console.error('Error restarting device:', error);
      setPinError(t('settings.developer.invalidUnlockCode'));
      setPin('');
      
      // Clear error message after a delay
      setTimeout(() => {
        setPinError(null);
      }, 2000);
      
      setRestarting(false);
    } finally {
      // Reset state after some time
      if (!showPinEntry) {
        setTimeout(() => {
          setRestarting(false);
          setMessage(null);
        }, 30000);
      }
    }
  };
  
  // Handle device shutdown with entered PIN
  const handleShutdownWithPin = async (enteredPin: string) => {
    if (shuttingDown) return;

    try {
      setShuttingDown(true);
      
      // Use the entered PIN as the unlock code
      const httpClient = new HttpClient(getFirmwareApiGateway());
      await httpClient.post('/device/shutdown', { 
        unlock_code: enteredPin,
      });
      
      // Close PIN dialog and show success message
      setShowPinEntry(false);
      setMessage(t('settings.developer.shutdownInitiated'));
      
      // We might lose connection to the device after shutdown
      setTimeout(() => {
        setMessage(t('settings.developer.deviceShuttingDown'));
      }, 3000);
      
    } catch (error) {
      console.error('Error shutting down device:', error);
      setPinError(t('settings.developer.invalidUnlockCode'));
      setPin('');
      
      // Clear error message after a delay
      setTimeout(() => {
        setPinError(null);
      }, 2000);
      
      setShuttingDown(false);
    } finally {
      // Reset state after some time
      if (!showPinEntry) {
        setTimeout(() => {
          setShuttingDown(false);
          setMessage(null);
        }, 30000);
      }
    }
  };

  return (
    <StyledCard width={380} height={432}>
      <YStack flex={1} justifyContent="flex-start" alignItems="center" gap="$4">
        {!showPinEntry && (
          <Heading color={tokens.color.white12} fontFamily="$heading" fontSize="$6" marginBottom="$4">
            {t('settings.developer.title')}
          </Heading>
        )}

        {!showPinEntry ? (
          <>
            {/* Restart Button */}
            <Button 
              backgroundColor={tokens.color.gray4}
              marginBottom="$2" 
              width={340} 
              height={60}
              borderRadius={30}
              fontFamily="$heading"
              disabled={restarting || shuttingDown}
              onPress={handleRestartClick}
              pressStyle={{ opacity: 0.8 }}
              justifyContent="flex-start"
              paddingLeft="$5"
              icon={<RefreshCw size={28} color={tokens.color.white12} strokeWidth={1.5} />}
            >
              <Text color={tokens.color.white12} fontFamily="$body" fontSize="$6" fontWeight="500" marginLeft="$2">
                {t('settings.developer.restart')}
              </Text>
            </Button>
            
            {/* Shutdown Button */}
            <Button 
              backgroundColor={tokens.color.gray4}
              marginBottom="$2" 
              width={340} 
              height={60}
              borderRadius={30}
              fontFamily="$heading"
              disabled={restarting || shuttingDown}
              onPress={handleShutdownClick}
              pressStyle={{ opacity: 0.8 }}
              justifyContent="flex-start"
              paddingLeft="$5"
              icon={<Power size={28} color={tokens.color.white12} strokeWidth={1.5} />}
            >
              <Text color={tokens.color.white12} fontFamily="$body" fontSize="$6" fontWeight="500" marginLeft="$2">
                {t('settings.developer.shutdown')}
              </Text>
            </Button>

            {/* Status Message */}
            {message && (
              <Text color={tokens.color.white12} fontFamily="$body" fontSize="$3" textAlign="center" marginTop="$2">
                {message}
              </Text>
            )}
          </>
        ) : (
          <YStack alignItems="center" gap="$4" width="100%" paddingHorizontal="$2">
            <Text color={tokens.color.white12} fontFamily="$body" fontSize="$5" textAlign="center">
              {t('settings.developer.enterUnlockCode')}
            </Text>
            
            {/* PIN indicator */}
            <YStack alignItems="center" paddingVertical="$2">
              <XStack gap="$2">
                {Array.from({ length: PIN_LENGTH }, (_, index) => (
                  <Circle
                    key={index}
                    width={20}
                    height={20}
                    backgroundColor={index < pin.length ? tokens.color.white12 : 'transparent'}
                    borderWidth={2}
                    borderColor={tokens.color.white12}
                  />
                ))}
              </XStack>
              <Text
                height={24}
                fontFamily="$body"
                fontSize="$3"
                color={pinError ? tokens.color.energyRed : 'transparent'}
                marginTop="$2"
              >
                {pinError || ' '}
              </Text>
            </YStack>
            
            {/* PIN keypad */}
            <YStack width="100%" alignItems="center" gap="$2">
              {[[1, 2, 3], [4, 5, 6], [7, 8, 9], [0]].map((row, rowIndex) => (
                <XStack key={rowIndex} justifyContent="center" gap="$3">
                  {row.map((number) => (
                    <Button
                      key={number}
                      width={50}
                      height={50}
                      backgroundColor={tokens.color.gray3}
                      borderRadius="50%"
                      alignItems="center"
                      onPress={() => handleNumberClick(number.toString())}
                      pressStyle={{ transform: [{ scale: 0.95 }] }}
                    >
                      <Text fontFamily="$body" fontSize="$5" color={tokens.color.white12}>
                        {number}
                      </Text>
                    </Button>
                  ))}
                </XStack>
              ))}
            </YStack>
          </YStack>
        )}
      </YStack>
    </StyledCard>
  );
};
